import { SelectionModel } from '@angular/cdk/collections';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatSelectChange } from '@angular/material/select';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import moment from 'moment';
import { filter, finalize, take } from 'rxjs/operators';

import { UserService } from 'src/app/core/api/user.service';
import { DialogConfirmPaymentComponent } from 'src/app/shared/dialogs/dialog-confirm-payment/dialog-confirm-payment.component';
import { SpinnerService } from 'src/app/shared/spinner/spinner.service';

import { CohortService } from '../../../core/api/cohort.service';
import { PaymentService } from '../../../core/api/payment.service';
import { RegionService } from '../../../core/api/region.service';
import { SiteService } from '../../../core/api/site.service';
import { UtilitiesService } from '../../../core/api/utlities.service';
import { CsvService } from '../../../core/helper/csv.service';
import { FormatHelperService } from '../../../core/helper/format-helper.service';
import { SiteHelperService } from '../../../core/helper/site.service';
import { StatusEnum } from '../../../shared/constants/site_status.enum';
import { Buyer, PaymentPeriod, PaymentPeriodSummary, User } from '../../../shared/models';
import * as fromApp from '../../../store';
import { selectAdminFiltersAndPage } from '../../../store/admin/admin.selectors';
import { selectCurrentUser } from '../../../store/auth/auth.selectors';
import { AdminTableBase } from '../admin-table-base/admin-table-base';

@Component({
  selector: 'fc-payment-dashboard',
  templateUrl: './payment-dashboard.component.html',
  styleUrls: ['./payment-dashboard.component.scss'],
})
export class PaymentDashboardComponent extends AdminTableBase implements OnInit {
  destroyRef = inject(DestroyRef);
  math = Math;
  readonly PAGE_NAME: string = 'payments';

  displayedColumns: string[] = [
    'select',
    'startDate',
    'endDate',
    'pendingNetPayment',
    'paidNetPayment',
    'pendingNetErt',
    'paidNetErt',
    'grossErt',
    'acrBufferErt',
    'internalBufferErt',
    'deductionErt',
    'averagePpt',
  ];

  dataSource: MatTableDataSource<PaymentPeriodSummary>;
  selection = new SelectionModel<PaymentPeriodSummary>(true, []);
  currentUser: User;

  totalArea = 0;
  totalAnnualPayment = 0;

  paymentPeriods: Array<PaymentPeriodSummary>;
  canPaySites: boolean = false;
  includeAllSubmitted: boolean = false;
  adminBuyers: Buyer[] | null = [];

  constructor(
    public dialog: MatDialog,
    readonly siteService: SiteService,
    readonly siteHelper: SiteHelperService,
    readonly csvService: CsvService,
    readonly formatService: FormatHelperService,
    readonly regionService: RegionService,
    readonly utilitiesService: UtilitiesService,
    readonly store: Store<fromApp.AppState>,
    readonly router: Router,
    readonly userService: UserService,
    readonly cohortService: CohortService,
    readonly paymentService: PaymentService,
    readonly spinnerService: SpinnerService
  ) {
    super();
  }

  ngOnInit() {
    this.store
      .select(selectCurrentUser)
      .pipe(
        filter(currentUser => currentUser !== null),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(currentUser => {
        this.currentUser = currentUser;
        this.canPaySites = currentUser.canPaySites;
        this.adminBuyers = currentUser.adminBuyers
          ? currentUser?.adminBuyers?.map(userAdminBuyer => userAdminBuyer.buyer)
          : null;
      });

    this.store
      .select(selectAdminFiltersAndPage)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(state => {
        super.setUpAdminFilter(state.filters);
        this.initializeFilters();
        this.queryItems();
      });
  }

  getFullQueryString(): string {
    return this.getBaseQueryString() + this.filterQuery;
  }

  queryItems(): void {
    // always include pagination
    this.spinnerService.show('app-spinner', true, 'Updating Periods');
    this.siteService
      .getFilteredPaymentPeriods(this.getFullQueryString(), this.includeAllSubmitted)
      .pipe(finalize(() => this.spinnerService.hide('app-spinner')))
      .subscribe(paymentPeriods => {
        this.clearSelection();
        this.paymentPeriods = paymentPeriods.results;
        this.totalItems = paymentPeriods.total;
        this.dataSource = new MatTableDataSource(this.paymentPeriods);
        this.setAllPages();
        this.setStartEndIndexes();
      });
  }

  onCSVExport() {
    let csv_string =
      'Start Date,End Date,Pending Payments,Pending ERT,Paid Payments,Paid ERT,Gross ERT,ACR Buffer,Internal Buffer,Deduction ERT,Average PPT\n';
    this.dataSource.data.forEach(row => {
      if (this.selection.isSelected(row)) {
        csv_string +=
          '"' +
          row.startDate +
          '","' +
          row.endDate +
          '","' +
          this.formatService.formatCurrencyLocal(row.netPendingPayout) +
          '","' +
          (row.netPendingErt ? row.netPendingErt : 0) +
          '","' +
          this.formatService.formatCurrencyLocal(row.netPaidPayout) +
          '","' +
          (row.netPaidErt ? row.netPaidErt : 0) +
          '","' +
          (row.grossErt ? row.grossErt : 0) +
          '","' +
          (row.acrBufferErt ? row.acrBufferErt : 0) +
          '","' +
          (row.internalBufferErt ? row.internalBufferErt : 0) +
          '","' +
          (row.deductionErt ? row.deductionErt : 0) +
          '","' +
          this.formatService.formatCurrencyLocal(row.averagePpt) +
          '"\n';
      }
    });
    this.csvService.exportCSV(csv_string, 'Payment_Summary');
  }

  onCSVDetailExport() {
    let csv_string =
      'Start Date,End Date,ACR Site,ACR RP, Submitted Date,Site ID,Site Name,Cohort Name,Pending Payments,Pending ERT,Paid Payments,Paid ERT,Gross ERT,ACR Buffer,Deduction ERT,ACR NET ERT,Internal Buffer,Average PPT,Site Status\n';
    let periods: Array<PaymentPeriodSummary> = [];
    this.dataSource.data.forEach(row => {
      if (this.selection.isSelected(row)) {
        periods.push(row);
      }
    });
    this.siteService.getDetailedPaymentPeriod(periods, this.includeAllSubmitted).subscribe(paymentPeriodDetails => {
      paymentPeriodDetails.forEach(detail => {
        let acr_net = detail.grossErt ? detail.grossErt : 0;
        acr_net += detail.acrBufferErt ? detail.acrBufferErt : 0;
        acr_net += detail.deductionErt ? detail.deductionErt : 0;
        csv_string +=
          '"' +
          detail.startDate +
          '","' +
          detail.endDate +
          '","' +
          (detail.acrProjectName ? detail.acrProjectName : '') +
          '","' +
          this.getRPNum(detail.cohortStartDate, detail.endDate) +
          '","' +
          detail.submittedDate +
          '","' +
          detail.siteId +
          '","' +
          detail.siteName +
          '","' +
          detail.cohortName +
          '","' +
          this.formatService.formatCurrencyLocal(detail.netPendingPayout) +
          '","' +
          detail.netPendingErt +
          '","' +
          this.formatService.formatCurrencyLocal(detail.netPaidPayout) +
          '","' +
          detail.netPaidErt +
          '","' +
          detail.grossErt +
          '","' +
          detail.acrBufferErt +
          '","' +
          detail.deductionErt +
          '","' +
          acr_net +
          '","' +
          detail.internalBufferErt +
          '","' +
          this.formatService.formatCurrencyLocal(detail.averagePpt) +
          '","' +
          detail.status +
          '"\n';
      });
      this.csvService.exportCSV(csv_string, 'Payment_detail');
    });
  }

  onBulkPayment() {
    let periods: Array<PaymentPeriodSummary> = [];
    this.dataSource.data.forEach(row => {
      if (this.selection.isSelected(row)) {
        periods.push(row);
      }
    });
    let buyers: Set<number> = new Set();
    this.siteService.getDetailedPaymentPeriod(periods, this.includeAllSubmitted).subscribe(paymentPeriodDetails => {
      let calcs = {
        payments: [],
        totalPaid: 0.0,
      };
      let alreadyPaid = 0;
      paymentPeriodDetails.forEach(detail => {
        if (
          detail.netPendingPayout != 0 &&
          detail.stripeAccountCreationToken !== null &&
          detail.reportResponseId !== null &&
          detail.status === StatusEnum.PAYMENT_READY &&
          moment(detail.endDate).isBefore(moment())
        ) {
          calcs.totalPaid += detail.netPendingPayout;
          buyers.add(detail.buyerId);
          calcs.payments.push({
            siteId: detail.siteId,
            name: detail.siteName,
            amount: detail.netPendingPayout,
            buyerId: detail.buyerId,
            paymentPeriod: new PaymentPeriod({
              startDate: detail.startDate,
              endDate: detail.endDate,
              sequence: detail.sequence,
              siteVersionId: detail.siteVersionId,
            }),
          });
        } else if (detail.netPendingPayout == 0) {
          alreadyPaid++;
        }
      });
      let dialogRef = this.dialog.open(DialogConfirmPaymentComponent, {
        panelClass: 'fc-dialog-confirm-payment',
        data: {
          buyers: [...buyers],
          alreadyPaid: alreadyPaid,
          calcs: calcs,
        },
        autoFocus: false,
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          const sites = calcs.payments.map(p => ({
            site_id: p.siteId,
            period: p.paymentPeriod,
          }));
          this.spinnerService.show('app-spinner', true, 'Making Payments');
          this.paymentService.paySites(sites).subscribe(resp => {
            this.spinnerService.hide('app-spinner');
            this.displayPaymentResults(resp, this.dialog);
            this.queryItems();
          });
        }
      });
    });
  }

  toggleIncludeAllSubmitted() {
    this.includeAllSubmitted = !this.includeAllSubmitted;
    this.queryItems();
  }

  private getRPNum(startDate: Date, endDate: Date) {
    startDate = new Date(startDate);
    endDate = new Date(endDate);
    const startYear = startDate.getFullYear();
    const startMonth = startDate.getMonth();
    const endYear = endDate.getFullYear();
    const endMonth = endDate.getMonth();

    const totalMonths = (endYear - startYear) * 12 + (endMonth - startMonth);
    const halfYears = totalMonths / 6;

    return Math.ceil(halfYears);
  }

  bulkAction($event: MatSelectChange) {
    switch ($event.value) {
      case 'pay':
        this.onBulkPayment();
        break;
      case 'csv':
        this.onCSVExport();
        break;
      case 'details':
        this.onCSVDetailExport();
        break;
      default:
        console.log('No Action found');
    }
    $event.source.value = '';
  }
}
